import React, { useEffect } from 'react';
import moment from 'moment';
import isEqual from 'lodash/isEqual';
import { connect } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  reduxForm,
  formValueSelector,
  Field,
  getFormSyncErrors,
  FieldArray,
} from 'redux-form';
import {
  renderSelectField,
  renderDateField,
} from '../../../utils/redux-form-helper';
import { Tooltip } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import validate from './validate';
import messages from './messages';
import { ElementWithPermissions } from '../../../components/ElementWithPermissions';
import type { UserPermission } from '../../App/types';
import ProRatedAmounts from './ProRatedAmounts';
import { useFetchHasNonMoveOutOpenCerts } from './hooks';
import { isUpdatedHudGrcLogicEnabled } from '../../QualificationForms/utils';
import { useHudRecertDueDate } from '../hooks';
import { isHudRecertOverdueForMoveOut } from '../utils';
import snapshotMessages from '../Snapshot/messages';

const yesNoMaybe = [
  { value: 'choose', text: 'Choose' },
  { value: true, text: 'Yes' },
  { value: false, text: 'No' },
];

// The move-out date is a local calendar value from the form and the GRC
// effective date is a UTC DATEONLY string, so normalize both to a UTC day
// before comparing to avoid a timezone off-by-one. Returns false when either
// date is missing or invalid.
const isMoveOutOnOrBeforeGrcEffectiveDate = (moveOutDate, effectiveDate) => {
  const moveOut = moment(moveOutDate);
  const effective = moment.utc(effectiveDate);

  if (!moveOut.isValid() || !effective.isValid()) {
    return false;
  }

  const moveOutUtcDay = moment.utc({
    year: moveOut.year(),
    month: moveOut.month(),
    day: moveOut.date(),
  });

  return moveOutUtcDay.isSameOrBefore(effective, 'day');
};

type Props = {
  intl: Object,
  cancel: Function,
  customerName: string,
  unitNumber: string,
  moveOutClick: Function,
  handleSubmit: Function,
  onSubmit: Function,
  valid: boolean,
  permissions: Array<UserPermission>,
  openFiscalPeriod: Object,
  isFirstFiscalPeriod: boolean,
  moveOutDateStatus: Function,
  formSyncErrors: any,
  monthlyTransactions: Array<any>,
  moveOutDate: any,
  change: Function,
  handleMoveOutDateUpdate: Function,
  isAllCommercial: boolean,
  moveInApplyFullAmountToProRate: boolean,
  moveOutProratedRent: boolean,
  roundProratedRents: boolean,
  organizationId: string,
  propertyId: string,
  residentId: string,
  isMoveOutRequestProcessing: boolean,
  backdateMoveOutDateMoreThan30DaysFlag?: boolean,
  isAffordable: boolean,
  hasGRCerts: boolean,
  hudRecertDueDate?: ?Object,
  isLoadingHudRecertDueDate?: boolean,
  isErrorHudRecertDueDate?: boolean,
  refetchHudRecertDueDate?: Function,
  touch?: Function,
};

export const ResidentMoveOut = ({
  cancel,
  intl,
  customerName,
  unitNumber,
  moveOutClick,
  onSubmit,
  handleSubmit,
  valid,
  permissions,
  formSyncErrors,
  moveOutDateStatus,
  monthlyTransactions,
  moveOutDate,
  change,
  handleMoveOutDateUpdate,
  isAllCommercial,
  moveInApplyFullAmountToProRate,
  moveOutProratedRent,
  roundProratedRents,
  organizationId,
  propertyId,
  residentId,
  isMoveOutRequestProcessing,
  isAffordable,
  hasGRCerts,
  hudRecertDueDate,
  isLoadingHudRecertDueDate = false,
  isErrorHudRecertDueDate = false,
  refetchHudRecertDueDate,
  touch,
}: Props) => {
  const hudRecertBlocksMoveOut = isHudRecertOverdueForMoveOut(
    moveOutDate,
    hudRecertDueDate,
  );
  useEffect(() => {
    if (hudRecertBlocksMoveOut) {
      touch?.('moveOutDate');
    }
  }, [hudRecertBlocksMoveOut, touch]);
  // Use useFetchHasNonMoveOutOpenCerts instead of useFetchHasOpenCerts to allow
  // move-out processing when only a Move-Out certification is open. This supports
  // the "Death of Sole Family Member" workflow where the Move-Out cert is started
  // before the actual move-out to meet TRACS reporting deadlines.
  const { data, isLoading: isLoadingHasOpenQualifications } =
    useFetchHasNonMoveOutOpenCerts({
      organizationId,
      propertyId,
      residentId,
      isAffordable,
    });

  const {
    hasNonGrossRentOpenCert = false,
    hasGrossRentOpenCert = false,
    openGrossRentCerts = [],
  } = data && typeof data === 'object' ? data : {};

  const flags = useFlags();
  const updatedHudGrcLogicEnabled = isUpdatedHudGrcLogicEnabled(flags);

  const grcWillBeCanceledOnMoveOut =
    updatedHudGrcLogicEnabled &&
    hasGrossRentOpenCert &&
    Boolean(moveOutDate) &&
    openGrossRentCerts.length > 0 &&
    openGrossRentCerts.every(
      (grCert) =>
        !grCert.certificationCompletionDate &&
        isMoveOutOnOrBeforeGrcEffectiveDate(
          moveOutDate,
          grCert.voucherEffectiveDate,
        ),
    );

  // Once the open GRC will be canceled on move-out it no longer blocks it.
  const grossRentOpenCertBlocksMoveOut =
    hasGrossRentOpenCert && !grcWillBeCanceledOnMoveOut;

  const getStatus = (isValid: Object) => {
    return isValid
      ? 'status status-ball status-green'
      : 'status status-ball status-red';
  };

  return (
    <div
      className="resident-convert-form container-fluid"
      data-test="resident-move-out-form"
    >
      <div className="row">
        <div className="col-xs-6 col-sm-12">
          <a className="btn-text" onClick={cancel}>
            <i className="et-chevron-left" />
            {isAllCommercial
              ? intl.formatMessage(messages.returnToTenantProfile)
              : intl.formatMessage(messages.returnToResidentProfile)}
          </a>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12 col-sm-12">
          <h1>
            {isAllCommercial
              ? intl.formatMessage(messages.confirmTitleTenant)
              : intl.formatMessage(messages.confirmTitle)}{' '}
            {customerName}
          </h1>
          <h4>
            {intl.formatMessage(messages.unitNumber)}: {unitNumber}
          </h4>
        </div>
      </div>
      <div className="form-container padtop20 padbottom30">
        <div className="row">
          <div className="col-xs-12 col-sm-7">
            <h3>{intl.formatMessage(messages.stepsCompleted)}:</h3>
            <div className="checklist-small move-out-steps-checklist">
              <ul>
                <li>
                  <div className="item-property">
                    {intl.formatMessage(messages.noticeToVacateEntered)}
                  </div>
                  <div className="item-value text-right">
                    {intl.formatMessage(messages.entered)}
                    <span className={getStatus(true)} />
                  </div>
                </li>
              </ul>
              {isAffordable && (
                <ul>
                  <li>
                    <div className="item-property">
                      {intl.formatMessage(messages.certificationsOpen)}
                    </div>

                    <div className="item-value text-right">
                      <Tooltip
                        placement="top"
                        containerSX={{ width: '100%' }}
                        title={
                          hasNonGrossRentOpenCert
                            ? intl.formatMessage(
                                messages.certificationsOpenTooltip,
                              )
                            : null
                        }
                      >
                        {intl.formatMessage(
                          hasNonGrossRentOpenCert
                            ? messages.notCompleted
                            : messages.completed,
                        )}

                        <span className={getStatus(!hasNonGrossRentOpenCert)} />
                      </Tooltip>
                    </div>
                  </li>
                </ul>
              )}
              {isAffordable && hasGRCerts && (
                <ul>
                  <li>
                    <div className="item-property">
                      {intl.formatMessage(messages.grCertificationOpen)}
                      {grcWillBeCanceledOnMoveOut && (
                        <Tooltip
                          placement="top"
                          title={intl.formatMessage(
                            messages.grCertificationWillCancelTooltip,
                          )}
                          containerSX={{
                            display: 'inline-block',
                            verticalAlign: 'middle',
                            marginLeft: '5px',
                          }}
                        >
                          <AlertInfoIcon />
                        </Tooltip>
                      )}
                    </div>
                    <div className="item-value text-right">
                      {grcWillBeCanceledOnMoveOut ? (
                        <>
                          {intl.formatMessage(messages.canceledAtMoveOut)}
                          <span className="status status-ball status-dark-orange" />
                        </>
                      ) : (
                        <Tooltip
                          placement="top"
                          containerSX={{ width: '100%' }}
                          title={
                            grossRentOpenCertBlocksMoveOut
                              ? intl.formatMessage(
                                  messages.grCertificationOpenTooltip,
                                )
                              : null
                          }
                        >
                          {intl.formatMessage(
                            grossRentOpenCertBlocksMoveOut
                              ? messages.notCompleted
                              : messages.completed,
                          )}
                          <span
                            className={getStatus(
                              !grossRentOpenCertBlocksMoveOut,
                            )}
                          />
                        </Tooltip>
                      )}
                    </div>
                  </li>
                </ul>
              )}
            </div>
          </div>
          <div className="col-xs-12 col-sm-5">
            <h3>{intl.formatMessage(messages.moveOutActionsCompleted)}:</h3>
            <div className="row">
              <div className="col-md-8 col-xs-12 padtop5">
                <label>
                  {isAllCommercial
                    ? intl.formatMessage(messages.paidAmountDueTenant)
                    : intl.formatMessage(messages.paidAmountDue)}
                </label>
              </div>
              <div className="col-md-4 col-xs-12">
                <Field
                  name="residentPaidAmtDueAtMoveOut"
                  component={renderSelectField}
                  options={yesNoMaybe}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-8 col-xs-12 padtop5">
                <label>{intl.formatMessage(messages.turnedInKeys)}</label>
              </div>
              <div className="col-md-4 col-xs-12">
                <Field
                  name="turnedInKeys"
                  component={renderSelectField}
                  options={yesNoMaybe}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-8 col-xs-12 padtop5">
                <label>
                  {intl.formatMessage(messages.moveOutChecklistCompleted)}
                </label>
              </div>
              <div className="col-md-4 col-xs-12">
                <div className="form-group">
                  <Field
                    name="moveOutChecklistCompleted"
                    component={renderSelectField}
                    options={yesNoMaybe}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12 col-sm-7">
            <h3> {intl.formatMessage(messages.moveOutDate)}:</h3>
            <div className="checklist-small no-margin-bottom">
              <ul>
                <li>
                  <div className="item-property">
                    <div className="form-group">
                      <Field
                        name="moveOutDate"
                        component={renderDateField}
                        dateFormat="MM/DD/YYYY"
                        bsSize="lg"
                        onChange={handleMoveOutDateUpdate}
                      />
                      {isLoadingHudRecertDueDate ? (
                        <p className="help-block">
                          {intl.formatMessage(
                            snapshotMessages.processMoveOutHudRecertLoading,
                          )}
                        </p>
                      ) : null}
                      {isErrorHudRecertDueDate ? (
                        <p className="help-block text-danger">
                          {intl.formatMessage(
                            snapshotMessages.processMoveOutHudRecertLoadError,
                          )}{' '}
                          <a
                            className="btn-text"
                            onClick={() => refetchHudRecertDueDate?.()}
                          >
                            {intl.formatMessage(snapshotMessages.retry)}
                          </a>
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="item-value text-right">
                    {moveOutDateStatus(formSyncErrors)
                      ? moveOutDateStatus(formSyncErrors).statusText
                      : ''}
                    <span
                      className={
                        moveOutDateStatus(formSyncErrors)
                          ? moveOutDateStatus(formSyncErrors).valid
                          : false
                      }
                    />
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div className="col-xs-12 col-sm-7">
            <p>
              <strong>
                {intl.formatMessage(messages.proRateAmountDateNote)}
              </strong>
            </p>
          </div>
        </div>
        {moveOutProratedRent && (
          <div className="row padtop15">
            <div className="col-xs-12 col-lg-11">
              <h3>
                {intl.formatMessage(messages.proRateAmountsToLedgerHeader, {
                  type: moveInApplyFullAmountToProRate
                    ? intl.formatMessage(messages.full)
                    : intl.formatMessage(messages.proRate),
                })}
              </h3>
              <div className="table-scroll table-units-container">
                <FieldArray
                  name="proRatedTransactions"
                  component={ProRatedAmounts}
                  monthlyTransactions={monthlyTransactions}
                  moveOutDate={moveOutDate}
                  intl={intl}
                  moveInApplyFullAmountToProRate={
                    moveInApplyFullAmountToProRate
                  }
                  roundProratedRents={roundProratedRents}
                />
              </div>
              {!moveInApplyFullAmountToProRate && (
                <>
                  <p className="padtop20">
                    <strong>{intl.formatMessage(messages.note)}: </strong>
                    {intl.formatMessage(messages.proRateNote)}
                  </p>
                  {roundProratedRents && (
                    <p>
                      <strong>*</strong>
                      {intl.formatMessage(messages.roundProratedAmounts)}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="row padtop20">
        <div className="col-xs-6">
          <button className="btn btn-default pull-right" onClick={cancel}>
            {intl.formatMessage(messages.cancel)}
          </button>
        </div>
        <div className="col-xs-6">
          <ElementWithPermissions
            userPermissions={permissions}
            scope={['resident-move-out']}
          >
            <button
              className="btn btn-primary pull-left"
              onClick={() => moveOutClick(handleSubmit(onSubmit))}
              disabled={
                !valid ||
                isLoadingHudRecertDueDate ||
                isErrorHudRecertDueDate ||
                hudRecertBlocksMoveOut ||
                (isAffordable &&
                  (grossRentOpenCertBlocksMoveOut ||
                    hasNonGrossRentOpenCert ||
                    isLoadingHasOpenQualifications)) ||
                isMoveOutRequestProcessing
              }
            >
              {isAllCommercial
                ? intl.formatMessage(messages.moveTenantOutOfThisUnit)
                : intl.formatMessage(messages.moveResidentOutOfThisUnit)}
            </button>
          </ElementWithPermissions>
        </div>
      </div>
    </div>
  );
};

export const mapStateToProps = (
  {
    form,
    app: {
      currentUser: { permissions },
      selectedProperty,
    },
  }: Object,
  {
    initialValues,
    backdateMoveOutDateMoreThan30DaysFlag,
    hudRecertDueDate,
  }: Object,
): Object => {
  const selector = formValueSelector('processMoveOut');
  return {
    propertyId: selectedProperty?.id,
    organizationId: selectedProperty?.organizationId,
    initialValues,
    permissions,
    moveOutDate: selector({ form }, 'moveOutDate'),
    formSyncErrors: getFormSyncErrors('processMoveOut')({ form }),
    isAllCommercial: selectedProperty.hasCommercialFloorPlans === 'ALL',
    backdateMoveOutDateMoreThan30DaysFlag,
    hudRecertDueDate,
  };
};

const shouldError = ({ values, nextProps, props, initialRender }) =>
  initialRender ||
  !isEqual(values, nextProps?.values) ||
  props?.hudRecertDueDate !== nextProps?.hudRecertDueDate;

const ResidentMoveOutForm = connect(mapStateToProps)(
  reduxForm({
    form: 'processMoveOut',
    validate,
    shouldError,
    enableReinitialize: true,
  })(ResidentMoveOut),
);

type ResidentMoveOutContainerProps = {
  organizationId?: string,
  propertyId?: string,
  householdId?: string,
  isHUDByUnit?: boolean,
  isSection236?: boolean,
  floorPlanAffordablePrograms?: Array<Object>,
  selectedProperty?: Object,
};

const ResidentMoveOutContainer = ({
  organizationId,
  propertyId,
  householdId,
  isHUDByUnit = false,
  isSection236 = false,
  floorPlanAffordablePrograms = [],
  selectedProperty = {},
  ...props
}: ResidentMoveOutContainerProps) => {
  const { hudRecertDueDate, isLoading, isError, refetch } = useHudRecertDueDate(
    {
      organizationId,
      propertyId,
      householdId,
      isHUDByUnit,
      isSection236,
      floorPlanAffordablePrograms,
      selectedProperty,
    },
  );

  return (
    <ResidentMoveOutForm
      {...props}
      hudRecertDueDate={hudRecertDueDate}
      isLoadingHudRecertDueDate={isLoading}
      isErrorHudRecertDueDate={isError}
      refetchHudRecertDueDate={refetch}
    />
  );
};

export default ResidentMoveOutContainer;
